import { IDashboard } from '../models/Dashboard'
import { HttpRequest } from "../utils"

const httpRequest = new HttpRequest()

export const get = async (data: { date: any; startDate: any; endDate: any; pieFilter: any }) => {
  return httpRequest.get<IDashboard>(`/ord/dashboard/main`, data)
}

export const topProducts = async (data: { date: string }) => {
  return httpRequest.get<{ name: string; count: number }[]>("/ord/dashboard/top-products", data)
}

// Field names are the backend's (logics/dashboard/delivery-stats.ts) — the
// old declaration invented deliveryRate / avgDeliveryDays, which never arrive.
export const deliveryStats = async (data: { date: string }) => {
  return httpRequest.get<{ completionRate: number; averageDeliveryDays: number }>("/ord/dashboard/delivery-stats", data)
}

type ChangeKpi = { value: number; currency?: "MNT"; momPct: number | null; yoyPct: number | null }
type SimpleChangeKpi = { value: number; currency?: "MNT"; momPct: number | null }
type DeltaKpi = { value: number; momDelta: number | null }
type Period = { from: string; to: string }

export type SalesAnalyticsSummary = {
  data: {
    totalSales: ChangeKpi
    orderCount: ChangeKpi
    avgOrder:   SimpleChangeKpi
    discount:   SimpleChangeKpi
    frequency:  DeltaKpi
  }
  meta: {
    period: Period
    previousPeriod: Period
    previousYearPeriod: Period
    currency: "MNT"
    appliedFilters: Record<string, string>
  }
}

export const salesAnalyticsSummary = async (data: { year: number; month: number; emp?: string | null }) => {
  return httpRequest.get<SalesAnalyticsSummary>("/ord/dashboard/sales-analytics/summary", data as any)
}

export type TrendBucket = { label: string; periodStart: string; value: number | null }

export type SalesAnalyticsTrend = {
  data: {
    granularity: "month"
    current: TrendBucket[]
    previousYear?: TrendBucket[]
  }
  meta: {
    currency: "MNT"
    appliedFilters: Record<string, string>
  }
}

export const salesAnalyticsTrend = async (data: { year: number; month: number; emp?: string | null; granularity?: "month"; yoy?: 1 }) => {
  return httpRequest.get<SalesAnalyticsTrend>("/ord/dashboard/sales-analytics/trend", data as any)
}

export type TopCustomerBreakdown = {
  productId: string
  productName: string
  qty: number
  amount: number
}

export type TopCustomerRow = {
  partnerId: string
  partnerName: string
  totalAmount: number
  orderCount: number
  yoyPct: number | null
  breakdown: TopCustomerBreakdown[]
}

export type SalesAnalyticsTopCustomers = {
  data: { rows: TopCustomerRow[] }
  meta: {
    period: { from: string; to: string }
    previousYearPeriod: { from: string; to: string }
    currency: "MNT"
    appliedFilters: Record<string, string>
  }
}

export const salesAnalyticsTopCustomers = async (data: { year: number; month: number; emp?: string | null }) => {
  return httpRequest.get<SalesAnalyticsTopCustomers>("/ord/dashboard/sales-analytics/top-customers", data as any)
}

export type TopProductBreakdown = {
  partnerId: string
  partnerName: string
  qty: number
  amount: number
}

export type TopProductRow = {
  productId: string
  productName: string
  totalAmount: number
  totalQty: number
  breakdown: TopProductBreakdown[]
}

export type SalesAnalyticsTopProducts = {
  data: { rows: TopProductRow[] }
  meta: {
    period: { from: string; to: string }
    currency: "MNT"
    appliedFilters: Record<string, string>
  }
}

export const salesAnalyticsTopProducts = async (data: { year: number; month: number; emp?: string | null }) => {
  return httpRequest.get<SalesAnalyticsTopProducts>("/ord/dashboard/sales-analytics/top-products", data as any)
}

export type CategoryRow = {
  categoryId: string
  name: string
  amount: number
  percent: number
}

export type SalesAnalyticsByCategory = {
  data: { rows: CategoryRow[]; total: number }
  meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> }
}

export const salesAnalyticsByCategory = async (data: { year: number; month: number; emp?: string | null }) => {
  return httpRequest.get<SalesAnalyticsByCategory>("/ord/dashboard/sales-analytics/by-category", data as any)
}

export type IncompleteOrderRow = {
  orderId: string
  orderNumber: string
  partnerName: string
  lineCount: number
  respondedDate: string
  orderAmount: number
  daysOpen: number
}

export type SalesAnalyticsIncompleteOrders = {
  data: { rows: IncompleteOrderRow[] }
  meta: {
    period: { from: string; to: string }
    minDaysOpen: number
    currency: "MNT"
    appliedFilters: Record<string, string>
  }
}

export const salesAnalyticsIncompleteOrders = async (data: { year: number; month: number; emp?: string | null }) => {
  return httpRequest.get<SalesAnalyticsIncompleteOrders>("/ord/dashboard/sales-analytics/incomplete-orders", data as any)
}

// ============ Margin section ============

export type MarginSummary = {
  data: {
    totalMargin:      { value: number; currency: "MNT"; momPct: number | null; yoyPct: number | null }
    marginPct:        { value: number; momDelta: number | null }
    avgOrderMargin:   { value: number; currency: "MNT"; momPct: number | null }
    topMarginProduct: { productId: string; productName: string; marginAmount: number; marginPct: number } | null
    /** Lines left out of the margin because their cost is unknown (BE O54). Optional: older backends omit it. */
    costUnknownCount?: number
  }
  meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> }
}

export const salesAnalyticsMarginSummary = async (data: { year: number; month: number; emp?: string | null }) => {
  return httpRequest.get<MarginSummary>("/ord/dashboard/sales-analytics/margin-summary", data as any)
}

export type MarginTrendBucket = { label: string; periodStart: string; marginAmount: number | null; marginPct: number | null }

export type MarginTrend = {
  data: { granularity: "month"; current: MarginTrendBucket[]; previousYear?: MarginTrendBucket[] }
  meta: { currency: "MNT"; appliedFilters: Record<string, string> }
}

export const salesAnalyticsMarginTrend = async (data: { year: number; month: number; emp?: string | null; yoy?: 1 }) => {
  return httpRequest.get<MarginTrend>("/ord/dashboard/sales-analytics/margin-trend", data as any)
}

export type MarginCustomerBreakdown = { productId: string; productName: string; qty: number; sales: number; marginAmount: number; marginPct: number }
export type MarginCustomerRow = { partnerId: string; partnerName: string; sales: number; marginAmount: number; marginPct: number; breakdown: MarginCustomerBreakdown[] }
export type MarginByCustomer = { data: { rows: MarginCustomerRow[] }; meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> } }
export const salesAnalyticsMarginByCustomer = async (data: { year: number; month: number; emp?: string | null }) =>
  httpRequest.get<MarginByCustomer>("/ord/dashboard/sales-analytics/margin-by-customer", data as any)

export type MarginProductBreakdown = { partnerId: string; partnerName: string; qty: number; sales: number; marginAmount: number; marginPct: number }
export type MarginProductRow = { productId: string; productName: string; sales: number; marginAmount: number; marginPct: number; breakdown: MarginProductBreakdown[] }
export type MarginByProduct = { data: { rows: MarginProductRow[] }; meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> } }
export const salesAnalyticsMarginByProduct = async (data: { year: number; month: number; emp?: string | null }) =>
  httpRequest.get<MarginByProduct>("/ord/dashboard/sales-analytics/margin-by-product", data as any)

export type MarginBrandBreakdown = { productId: string; productName: string; qty: number; sales: number; marginAmount: number; marginPct: number }
export type MarginBrandRow = { brand: string; sales: number; marginAmount: number; marginPct: number; breakdown: MarginBrandBreakdown[] }
export type MarginByBrand = { data: { rows: MarginBrandRow[] }; meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> } }
export const salesAnalyticsMarginByBrand = async (data: { year: number; month: number; emp?: string | null }) =>
  httpRequest.get<MarginByBrand>("/ord/dashboard/sales-analytics/margin-by-brand", data as any)

export type MarginEmployeeBreakdown = { partnerId: string; partnerName: string; sales: number; marginAmount: number; marginPct: number }
export type MarginEmployeeRow = { userId: string; name: string; sales: number; marginAmount: number; marginPct: number; orderCount: number; breakdown: MarginEmployeeBreakdown[] }
export type MarginByEmployee = { data: { rows: MarginEmployeeRow[] }; meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> } }
export const salesAnalyticsMarginByEmployee = async (data: { year: number; month: number; emp?: string | null }) =>
  httpRequest.get<MarginByEmployee>("/ord/dashboard/sales-analytics/margin-by-employee", data as any)

export type LowMarginOrderRow = { orderId: string; orderNumber: string; partnerName: string; orderAmount: number; marginAmount: number; marginPct: number; respondedDate: string }
export type LowMarginOrders = { data: { rows: LowMarginOrderRow[] }; meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> } }
export const salesAnalyticsLowMarginOrders = async (data: { year: number; month: number; emp?: string | null }) =>
  httpRequest.get<LowMarginOrders>("/ord/dashboard/sales-analytics/low-margin-orders", data as any)

// ============ Returns section ============

export type ReturnsSummary = {
  data: {
    totalReturnAmount: { value: number; currency: "MNT"; momPct: number | null }
    returnCount      : { value: number; momDelta: number | null }
    returnRatePct    : { value: number; momDelta: number | null }
  }
  meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> }
}
export const salesAnalyticsReturnsSummary = async (data: { year: number; month: number; emp?: string | null }) =>
  httpRequest.get<ReturnsSummary>("/ord/dashboard/sales-analytics/returns-summary", data as any)

export type ReturnsTrendBucket = { label: string; periodStart: string; amount: number; count: number }
export type ReturnsTrend = { data: { rows: ReturnsTrendBucket[] }; meta: { currency: "MNT"; appliedFilters: Record<string, string> } }
export const salesAnalyticsReturnsTrend = async (data: { year: number; month: number; emp?: string | null }) =>
  httpRequest.get<ReturnsTrend>("/ord/dashboard/sales-analytics/returns-trend", data as any)

export type ReturnsByProductBreakdown = { partnerId: string; partnerName: string; reasonCode: string; reasonLabel: string; amount: number; qty: number; date: string }
export type ReturnsByProductRow = { productId: string; productName: string; qty: number; amount: number; breakdown: ReturnsByProductBreakdown[] }
export type ReturnsByProduct = { data: { rows: ReturnsByProductRow[] }; meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> } }
export const salesAnalyticsReturnsByProduct = async (data: { year: number; month: number; emp?: string | null }) =>
  httpRequest.get<ReturnsByProduct>("/ord/dashboard/sales-analytics/returns-by-product", data as any)

export type ReturnsByCustomerBreakdown = { productId: string; productName: string; qty: number; reasonCode: string; reasonLabel: string; amount: number }
export type ReturnsByCustomerRow = { partnerId: string; partnerName: string; qty: number; amount: number; breakdown: ReturnsByCustomerBreakdown[] }
export type ReturnsByCustomer = { data: { rows: ReturnsByCustomerRow[] }; meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> } }
export const salesAnalyticsReturnsByCustomer = async (data: { year: number; month: number; emp?: string | null }) =>
  httpRequest.get<ReturnsByCustomer>("/ord/dashboard/sales-analytics/returns-by-customer", data as any)

export type ReturnsByEmployeeBreakdown = { partnerId: string; partnerName: string; productName: string; reasonCode: string; reasonLabel: string; qty: number; amount: number }
export type ReturnsByEmployeeRow = { userId: string; name: string; qty: number; amount: number; breakdown: ReturnsByEmployeeBreakdown[] }
export type ReturnsByEmployee = { data: { rows: ReturnsByEmployeeRow[] }; meta: { period: { from: string; to: string }; currency: "MNT"; appliedFilters: Record<string, string> } }
export const salesAnalyticsReturnsByEmployee = async (data: { year: number; month: number; emp?: string | null }) =>
  httpRequest.get<ReturnsByEmployee>("/ord/dashboard/sales-analytics/returns-by-employee", data as any)
